import { useRaten } from "../hooks/usePflegekunden";
import type { Rate } from "../hooks/usePflegekunden";
import { eur } from "../hooks/useVertraege";
import { periodeDe } from "../lib/zahl";

const jetzt = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/** Stand einer Rate in einem Wort — Zahlung vor Einzug, Rückläufer vor allem. */
function stand(r: Rate): { text: string; ton: string } {
  if (r.einzug_status === "zurueck") return { text: "zurück", ton: "is-rot" };
  if (r.status === "bezahlt") return { text: "bezahlt", ton: "is-gruen" };
  if (r.status === "storniert") return { text: "storniert", ton: "" };
  if (r.einzug_status === "eingereicht") return { text: "eingezogen", ton: "is-blau" };
  if (r.periode.slice(0, 7) <= jetzt()) return { text: "fällig", ton: "is-gelb" };
  return { text: "geplant", ton: "" };
}

/**
 * Die Raten eines Vertrags (Etappe B, 09.10.2026). Bis dahin stand nirgends,
 * ob ein Kunde bezahlt hat — die Frage, mit der ein Inhaber die Akte öffnet.
 * Beträge brutto: eingezogen und überwiesen wird, was der Kunde zahlt.
 */
export default function RatenListe({ vertragId }: { vertragId: string }) {
  const { data: raten = [], isLoading, error } = useRaten(vertragId);
  if (isLoading) return <div className="pf-leer">Lade Raten…</div>;
  if (error) return <div className="pf-leer">Die Raten ließen sich nicht laden.</div>;
  if (!raten.length) {
    return <div className="pf-leer">Noch keine Raten — sie entstehen mit der Aktivierung des Vertrags.</div>;
  }
  const bezahlt = raten.filter(r => r.status === "bezahlt");
  const offen = raten.filter(r => r.status !== "bezahlt" && r.status !== "storniert" && r.periode.slice(0, 7) <= jetzt());
  const summe = (l: Rate[]) => l.reduce((s, r) => s + Number(r.soll_brutto ?? r.soll_netto ?? 0), 0);

  return (
    <div className="pf-raten">
      <p className="pf-raten-summe">
        {bezahlt.length} von {raten.length} {raten.length === 1 ? "Rate" : "Raten"} bezahlt ({eur(summe(bezahlt))})
        {offen.length > 0 ? ` · fällig und offen: ${eur(summe(offen))}` : ""}
      </p>
      <ul className="pf-raten-liste">
        {raten.map(r => {
          const s = stand(r);
          return (
            <li key={r.id}>
              <span className="pf-raten-monat">{periodeDe(r.periode.slice(0, 7))}</span>
              <span className="pf-raten-betrag">{eur(r.soll_brutto ?? r.soll_netto)}</span>
              <span className={"pf-pill " + s.ton}>{s.text}</span>
              {r.einzug_status === "zurueck" && r.zurueck_grund && (
                <span className="pf-raten-grund">{r.zurueck_grund}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
